import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { ArrowLeft, Clock, User, MessageCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ArticleDetail = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { user, isAuthenticated, login } = useAuth();

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const inline = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, '<strong style="color:#fff">$1</strong>');

// Convert plain-text/markdown-ish articles (learning path content) into styled HTML.
// Content that already contains HTML tags is passed through unchanged.
const formatContent = (content) => {
  if (!content || /<[a-z][\s\S]*>/i.test(content)) return content;
  const blocks = content.split(/\n\n+/);
  return blocks.map(block => {
    const b = block.trim();
    if (!b) return '';
    const plain = b.replace(/\*\*/g, '');
    if (/^(PRO TIP:|FUN FACT:)/i.test(plain)) {
      const isTip = /^PRO TIP:/i.test(plain);
      return `<div style="border-left:3px solid ${isTip ? '#C5A059' : '#007AFF'};background:${isTip ? 'rgba(197,160,89,0.07)' : 'rgba(0,122,255,0.07)'};padding:12px 16px;border-radius:8px;margin:16px 0;font-size:0.925rem"><strong style="color:${isTip ? '#C5A059' : '#3B9BFF'}">${isTip ? 'PRO TIP' : 'FUN FACT'}</strong> — ${inline(plain.replace(/^(PRO TIP:|FUN FACT:)\s*/i, ''))}</div>`;
    }
    const lines = b.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.every(l => l.startsWith('✓'))) {
      const items = lines.map(l => `<li style="margin:6px 0"><span style="color:#10B981;font-weight:700">✓</span> ${inline(l.replace(/^✓\s*/, ''))}</li>`).join('');
      return `<ul style="list-style:none;padding:14px 18px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);border-radius:10px;margin:16px 0">${items}</ul>`;
    }
    if (lines.length === 1 && plain.length < 90 && plain.endsWith(':')) {
      return `<h3 style="color:#fff;font-weight:700;font-size:1.2rem;margin:28px 0 8px">${esc(plain.slice(0, -1))}</h3>`;
    }
    // line-wise: group bullets, detect inline headers
    let html = '';
    let bullets = [];
    const flush = () => {
      if (bullets.length) {
        html += `<ul style="padding-left:22px;margin:10px 0">${bullets.map(l => `<li style="margin:5px 0">${inline(l)}</li>`).join('')}</ul>`;
        bullets = [];
      }
    };
    for (const line of lines) {
      const lp = line.replace(/\*\*/g, '');
      if (/^[-•]\s+/.test(line)) {
        bullets.push(line.replace(/^[-•]\s+/, ''));
      } else if (lp.length < 90 && lp.endsWith(':') && lines.length > 1) {
        flush();
        html += `<h3 style="color:#fff;font-weight:700;font-size:1.2rem;margin:28px 0 8px">${esc(lp.slice(0, -1))}</h3>`;
      } else {
        flush();
        html += `<p style="margin:12px 0">${inline(line)}</p>`;
      }
    }
    flush();
    return html;
  }).join('');
};

  useEffect(() => {
    fetchArticle();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/education/articles/${articleId}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/education/articles/${articleId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    if (!isAuthenticated) {
      toast.error('Please login to comment');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/education/articles/${articleId}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      setNewComment('');
      fetchComments();
      toast.success('Comment added');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A059]"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Article not found</h1>
          <Link to="/education">
            <Button className="bg-[#C5A059] text-black hover:bg-[#D4AF37]">
              Back to Education Hub
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/education" className="inline-flex items-center text-gray-400 hover:text-[#C5A059] transition-colors mb-8" data-testid="back-button">
            <ArrowLeft size={20} className="mr-2" />
            Back to Education Hub
          </Link>

          {/* Article Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/30 text-xs px-2 py-1 rounded-full">
                {article.category}
              </span>
              {article.tags?.map((tag, idx) => (
                <span key={idx} className="bg-white/10 text-white text-xs px-2 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-6" data-testid="article-title">
              {article.title}
            </h1>
            <div className="flex items-center gap-6 text-gray-400 text-sm">
              <div className="flex items-center gap-2">
                <User size={16} />
                <span>{article.author_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{new Date(article.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
            <div className="mt-5">
              <ShareButtons title={article.title} />
            </div>
          </div>

          {/* Featured Image */}
          {article.image && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Article Content */}
          <div className="prose prose-invert max-w-none" data-testid="article-content">
            <div className="text-gray-300 text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatContent(article.content)) }}
            />
          </div>

          {/* Comments Section */}
          <div className="mt-16 border-t border-white/10 pt-12">
            <h3 className="font-manrope font-bold text-2xl text-white mb-8 flex items-center gap-2">
              <MessageCircle size={24} className="text-[#C5A059]" />
              Discussion ({comments.length})
            </h3>

            {/* Comment Form */}
            {isAuthenticated ? (
              <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-8">
                <form onSubmit={handleCommentSubmit}>
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts on this article..."
                    className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] rounded-sm mb-4 min-h-[100px]"
                    data-testid="comment-input"
                  />
                  <Button
                    type="submit"
                    disabled={submitting || !newComment.trim()}
                    className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-6 py-2 font-semibold"
                    data-testid="comment-submit"
                  >
                    {submitting ? 'Posting...' : 'Post Comment'}
                  </Button>
                </form>
              </div>
            ) : (
              <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-8 text-center">
                <p className="text-gray-400 mb-4">Login to join the discussion</p>
                <Button
                  onClick={login}
                  className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-6 py-2 font-semibold"
                >
                  Login to Comment
                </Button>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No comments yet. Be the first to share your thoughts!</p>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.comment_id}
                    className="bg-[#111111] border border-white/10 rounded-lg p-6"
                    data-testid={`comment-${comment.comment_id}`}
                  >
                    <div className="flex gap-4">
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarImage src={comment.author_picture} alt={comment.author_name} />
                        <AvatarFallback className="bg-[#C5A059] text-black font-semibold">
                          {comment.author_name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-white">{comment.author_name}</span>
                          <span className="text-gray-500 text-xs">
                            {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                        <p className="text-gray-300 whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ArticleDetail;
